import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { adminApiService } from '../../services/adminApiService'
import { ApiError } from '../../services/BaseApiService'
import type { AdminJob, AdminJobsResponse, JobStatus } from '../../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../../styles/theme'
import AdminTabs from './AdminTabs'

const REFRESH_MS = 30_000

const STATUS: Record<JobStatus, { label: string; fg: string; bg: string }> = {
  running:   { label: 'Running',   fg: colors.info,          bg: colors.infoLight },
  succeeded: { label: 'Succeeded', fg: colors.success,       bg: colors.successLight },
  partial:   { label: 'Partial',   fg: colors.warning,       bg: colors.warningLight },
  failed:    { label: 'Failed',    fg: colors.error,         bg: colors.errorLight },
  never:     { label: 'Not run',   fg: colors.textSecondary, bg: colors.background },
}

export function every(seconds: number): string {
  if (seconds % 86400 === 0) return seconds === 86400 ? 'Daily' : `Every ${seconds / 86400} days`
  if (seconds % 3600 === 0) return seconds === 3600 ? 'Hourly' : `Every ${seconds / 3600} h`
  return `Every ${Math.round(seconds / 60)} min`
}

export function duration(ms: number | null): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`
}

export function relative(iso: string | null, now = Date.now()): string {
  if (!iso) return '—'
  const diff = new Date(iso).getTime() - now
  const abs = Math.abs(diff)
  const unit = abs < 90_000 ? `${Math.max(1, Math.round(abs / 1000))} s` : abs < 5_400_000 ? `${Math.round(abs / 60_000)} min`
    : abs < 129_600_000 ? `${Math.round(abs / 3_600_000)} h` : `${Math.round(abs / 86_400_000)} d`
  return diff < 0 ? `${unit} ago` : `in ${unit}`
}

const stamp = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : undefined)

/** The central Logs tab, filtered to one job (and optionally one run). */
export const logsFor = (job: string, run?: string | null) =>
  `/app/admin/logs?${new URLSearchParams({ source: 'jobs', job, ...(run ? { run } : {}) })}`

const link: React.CSSProperties = { color: colors.primary, fontSize: fontSize.sm, fontWeight: 500, textDecoration: 'none' }

export default function AdminJobsPage() {
  const [data, setData] = useState<AdminJobsResponse | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    try { setData(await adminApiService.getJobs()); setError('') }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Failed to load jobs') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, REFRESH_MS)
    return () => clearInterval(t)
  }, [load])

  const th: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, textAlign: 'left', fontFamily: fontFamily.mono, fontSize: fontSize.xs,
    fontWeight: 600, color: colors.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase',
    borderBottom: `1px solid ${colors.border}`, background: colors.background, whiteSpace: 'nowrap',
  }
  const td: React.CSSProperties = { padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, color: colors.text, borderBottom: `1px solid ${colors.border}`, verticalAlign: 'top' }
  const r = data?.renders

  return (
    <div>
      <AdminTabs />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.lg }}>
        <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, margin: 0 }}>Background jobs</h1>
        <button type="button" onClick={load} aria-label="Refresh"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: fontSize.sm, color: colors.text }}>
          <FontAwesomeIcon icon={faRotateRight} style={{ width: 12 }} /> Refresh
        </button>
      </div>
      <p style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 0, marginBottom: spacing.lg }}>
        Each job runs on exactly one API instance per interval. Refreshes every 30 seconds.
      </p>

      {error && <div role="alert" style={{ background: colors.errorLight, color: colors.error, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md }}>{error}</div>}

      {loading ? <div style={{ color: colors.textSecondary }}>Loading...</div> : data && (
        <>
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.sm, overflowX: 'auto', marginBottom: spacing.xl }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Job</th><th style={th}>Status</th><th style={th}>Last run</th><th style={th}>Duration</th>
                  <th style={th}>Result</th><th style={th}>Next</th><th style={th}>Schedule</th><th style={th}>Logs</th>
                </tr>
              </thead>
              <tbody>
                {data.jobs.map(j => <JobRow key={j.name} job={j} td={td} open={open === j.name} onToggle={() => setOpen(open === j.name ? null : j.name)} />)}
              </tbody>
            </table>
          </div>

          {r && (
            <section aria-label="Render queue">
              <h2 style={{ fontSize: fontSize.lg, fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>Render queue</h2>
              <p style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 0 }}>
                Async renders, each claimed by one instance. <Link to={logsFor('RenderWorker')} style={link}>Render logs</Link>
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(150px, 100%), 1fr))', gap: spacing.md }}>
                <Stat label="Waiting" value={r.pending} hint={r.oldestPendingAt ? `oldest ${relative(r.oldestPendingAt)}` : undefined} alert={r.pending > 0 && !!r.oldestPendingAt && Date.now() - new Date(r.oldestPendingAt).getTime() > 300_000} />
                <Stat label="Rendering now" value={r.inProgress} />
                <Stat label="Completed (24 h)" value={r.completed24h} />
                <Stat label="Failed (24 h)" value={r.failed24h} alert={r.failed24h > 0} />
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function JobRow({ job: j, td, open, onToggle }: { job: AdminJob; td: React.CSSProperties; open: boolean; onToggle: () => void }) {
  const s = STATUS[j.status] ?? STATUS.never
  const infoId = `job-info-${j.name}`
  return (
    <>
      <tr>
        <td style={{ ...td, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{j.title}</span>
            <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={infoId} aria-label={`About ${j.title}`} title={j.summary}
              style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: open ? colors.primary : colors.textMuted, display: 'inline-flex' }}>
              <FontAwesomeIcon icon={faCircleInfo} style={{ width: 14 }} />
            </button>
          </div>
          <div style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{j.summary}</div>
        </td>
        <td style={td}>
          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: fontSize.xs, fontWeight: 600, color: s.fg, background: s.bg, whiteSpace: 'nowrap' }}>{s.label}</span>
        </td>
        <td style={{ ...td, whiteSpace: 'nowrap' }} title={stamp(j.lastStartedAt)}>{relative(j.lastStartedAt)}</td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>{duration(j.lastDurationMs)}</td>
        <td style={{ ...td, minWidth: 220, color: j.lastError ? colors.error : colors.text }}>
          {j.lastError ?? j.lastSummary ?? (j.lastOutcome ? '—' : '')}
        </td>
        <td style={{ ...td, whiteSpace: 'nowrap' }} title={stamp(j.nextDueAt)}>{j.status === 'running' ? 'now' : j.nextDueAt ? relative(j.nextDueAt) : 'soon'}</td>
        <td style={{ ...td, whiteSpace: 'nowrap', color: colors.textSecondary }}>{every(j.intervalSeconds)}</td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {j.lastRunId && <Link to={logsFor(j.name, j.lastRunId)} style={link}>Last run</Link>}
            <Link to={logsFor(j.name)} style={link}>All runs</Link>
          </div>
        </td>
      </tr>
      {open && (
        <tr id={infoId}>
          <td colSpan={8} style={{ ...td, background: colors.background }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{j.summary}</div>
            <div style={{ color: colors.textSecondary, lineHeight: 1.5, maxWidth: 820 }}>{j.description}</div>
            <div style={{ fontSize: fontSize.xs, color: colors.textMuted, marginTop: spacing.xs, fontFamily: fontFamily.mono }}>
              {j.name}{j.runningOn ? ` · running on ${j.runningOn}` : ''}{j.lastFinishedAt ? ` · last finished ${stamp(j.lastFinishedAt)}` : ''}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function Stat({ label, value, hint, alert }: { label: string; value: number; hint?: string; alert?: boolean }) {
  return (
    <div style={{ background: colors.surface, border: `1px solid ${alert ? colors.error : colors.border}`, borderRadius: radius.md, padding: spacing.md, boxShadow: shadow.sm }}>
      <div style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>{label}</div>
      <div style={{ fontSize: fontSize.xl, fontWeight: 700, color: alert ? colors.error : colors.text }}>{value}</div>
      {hint && <div style={{ fontSize: fontSize.xs, color: colors.textMuted }}>{hint}</div>}
    </div>
  )
}
